import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Save, Pencil, Trash2, Plus, Key, KeyRound,
  User, UserCheck, UserMinus, UserPlus, ExternalLink, CheckCircle,
  Star, Package, Globe, Mail, Calendar, Tag, X, Check, Eye, EyeOff,
  ShieldAlert,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const SERVICES = ['webspace','domains','vserver','dedi','discordbots','musicbots','licenses','teamspeak','gameserver'];
const SERVICE_LABELS = { webspace:'Webspace', domains:'Domains', vserver:'VServer', dedi:'Dedicated',
  discordbots:'Discord Bots', musicbots:'Musicbots', licenses:'Lizenzen', teamspeak:'TeamSpeak', gameserver:'Gameserver' };
const CATEGORIES = ['webspace','vserver','dedi','gameserver','teamspeak','discordbots','musicbots','domains','other'];
const PERIODS = [{ value:'monthly', label:'/ Monat' }, { value:'yearly', label:'/ Jahr' }, { value:'one-time', label:'einmalig' }];

const TABS = [
  { id:'profile',  label:'Profil',            icon: Globe },
  { id:'products', label:'Produkte',           icon: Package },
  { id:'account',  label:'Account & API-Keys', icon: Key },
  { id:'reviews',  label:'Bewertungen',        icon: Star },
];

const inputCls = "w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors";
const labelCls = "block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5";

/* ── Profil Tab ──────────────────────────────────────────────────────────── */
function ProfileTab({ hid, authHeaders, API }) {
  const [data, setData]     = useState(null);
  const [form, setForm]     = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/hostings/${hid}`, { headers: authHeaders() });
    setData(r.data); setForm({ ...r.data });
  }, [hid, API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleService = k => setForm(f => ({ ...f, [k]: f[k] ? 0 : 1 }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/admin/hostings/${hid}`, form, { headers: authHeaders() });
      setMsg('Gespeichert'); load();
    } catch (err) { setMsg(err.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="provider-profile-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className={labelCls}>Name *</label>
          <input value={form.name || ''} onChange={e => set('name', e.target.value)} required data-testid="profile-name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Website</label>
          <input value={form.url || ''} onChange={e => set('url', e.target.value)} type="url" data-testid="profile-url" placeholder="https://" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Logo-URL</label>
          <input value={form.logo_url || ''} onChange={e => set('logo_url', e.target.value)} data-testid="profile-logo" placeholder="https://..." className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Tagline (Kurzslogan)</label>
          <input value={form.tagline || ''} onChange={e => set('tagline', e.target.value)} data-testid="profile-tagline" placeholder="Günstiges Hosting für alle" className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Beschreibung</label>
          <textarea value={form.description || ''} onChange={e => set('description', e.target.value)}
            data-testid="profile-description" rows={4} placeholder="Ausführliche Beschreibung des Anbieters..."
            className={inputCls + " resize-none"} />
        </div>
        <div>
          <label className={labelCls}>Kontakt-E-Mail</label>
          <input value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} type="email" data-testid="profile-email" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Gründungsjahr</label>
          <input value={form.founded_year || ''} onChange={e => set('founded_year', e.target.value ? Number(e.target.value) : null)}
            type="number" min="1990" max="2030" data-testid="profile-year" placeholder="z.B. 2018" className={inputCls} />
        </div>
      </div>

      {/* Services */}
      <div className="bg-slate-800/40 border border-slate-700/60 rounded-xl p-4">
        <p className={labelCls}>Angebotene Dienste</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {SERVICES.map(k => (
            <button key={k} type="button" onClick={() => toggleService(k)}
              data-testid={`service-${k}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${
                form[k]
                  ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400'
                  : 'bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300'
              }`}>
              {form[k] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 opacity-30" />}
              {SERVICE_LABELS[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} data-testid="profile-save-btn"
          className="flex items-center gap-2 px-6 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Profil speichern
        </button>
        {msg && <span className={`text-sm ${msg === 'Gespeichert' ? 'text-green-400' : 'text-red-400'}`}>{msg}</span>}
      </div>
    </form>
  );
}

/* ── Produkte Tab ─────────────────────────────────────────────────────────── */
const EMPTY_PRODUCT = { name:'', description:'', price:0, price_period:'monthly', category:'webspace', url:'', active:1,
  cpu_type:'', cpu_cores:'', ram_type:'', ram_size:'', storage_type:'', storage_size:'', bandwidth:'', ddos_protection:'', traffic:'' };

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...EMPTY_PRODUCT, ...initial });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const SPECS = [
    ['cpu_type','CPU-Typ'],['cpu_cores','CPU-Kerne'],['ram_type','RAM-Typ'],['ram_size','RAM-Größe'],
    ['storage_type','Speicher-Typ'],['storage_size','Speicher-Größe'],['bandwidth','Anbindung'],
    ['ddos_protection','DDoS-Schutz'],['traffic','Inkl. Traffic'],
  ];
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 space-y-4" data-testid="product-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Produktname *</label>
          <input value={f.name} onChange={e => set('name', e.target.value)} required data-testid="product-form-name" className={inputCls} placeholder="z.B. VServer S" />
        </div>
        <div>
          <label className={labelCls}>Preis (€)</label>
          <input value={f.price} onChange={e => set('price', parseFloat(e.target.value) || 0)} type="number" step="0.01" min="0" data-testid="product-form-price" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Zeitraum</label>
          <select value={f.price_period} onChange={e => set('price_period', e.target.value)} className={inputCls}>
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Kategorie</label>
          <select value={f.category} onChange={e => set('category', e.target.value)} className={inputCls}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={f.active} onChange={e => set('active', Number(e.target.value))} className={inputCls}>
            <option value={1}>Aktiv</option>
            <option value={0}>Inaktiv</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Produkt-URL</label>
          <input value={f.url} onChange={e => set('url', e.target.value)} type="url" placeholder="https://" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Beschreibung</label>
          <textarea value={f.description} onChange={e => set('description', e.target.value)} rows={2} className={inputCls + " resize-none"} />
        </div>
      </div>

      {/* Tech Specs */}
      <details className="group">
        <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-300 transition-colors select-none">
          Technische Spezifikationen (optional)
        </summary>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {SPECS.map(([k, lbl]) => (
            <div key={k}>
              <label className="text-xs text-slate-500 block mb-1">{lbl}</label>
              <input value={f[k]} onChange={e => set(k, e.target.value)} className={inputCls} placeholder="–" />
            </div>
          ))}
        </div>
      </details>

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={() => onSave(f)} disabled={saving || !f.name.trim()}
          data-testid="product-form-save"
          className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm transition-colors">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function ProductsTab({ hid, authHeaders, API }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);   // product id or 'new'
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/hostings/${hid}/products`, { headers: authHeaders() });
    setProducts(r.data); setLoading(false);
  }, [hid, API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/hostings/${hid}/products`, form, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/admin/hostings/${hid}/products/${editing}`, form, { headers: authHeaders() });
      }
      setEditing(null); load();
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const del = async (pid, name) => {
    if (!window.confirm(`Produkt "${name}" löschen?`)) return;
    await axios.delete(`${API}/admin/hostings/${hid}/products/${pid}`, { headers: authHeaders() });
    load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{products.length} Produkt{products.length !== 1 ? 'e' : ''}</p>
        <button onClick={() => setEditing('new')} data-testid="add-product-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm">
          <Plus className="w-4 h-4" /> Produkt hinzufügen
        </button>
      </div>

      {editing === 'new' && (
        <ProductForm initial={EMPTY_PRODUCT} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
      )}

      {products.length === 0 && editing !== 'new' && (
        <div className="text-center py-12 text-slate-500 text-sm">Noch keine Produkte vorhanden.</div>
      )}

      <div className="space-y-3">
        {products.map(p => (
          <div key={p.id}>
            {editing === p.id ? (
              <ProductForm initial={p} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
            ) : (
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-800/40 border border-slate-700/60 rounded-xl"
                data-testid={`product-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.active ? 'bg-green-400' : 'bg-slate-600'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{p.name || <span className="text-slate-500 italic">Kein Name</span>}</p>
                    <p className="text-xs text-slate-500">
                      {p.category} · {p.price > 0 ? `${Number(p.price).toFixed(2)} €` : 'Kostenlos'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => setEditing(p.id)} data-testid={`edit-product-${p.id}`}
                    className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-700 rounded-lg transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(p.id, p.name)} data-testid={`delete-product-${p.id}`}
                    className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Account & API-Keys Tab ───────────────────────────────────────────────── */
function AccountTab({ hid, authHeaders, API }) {
  const [account,  setAccount]  = useState(null);
  const [apiKeys,  setApiKeys]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [invForm,  setInvForm]  = useState({ name:'', email:'', password:'' });
  const [newKey,   setNewKey]   = useState({ name:'', result:null });
  const [resetPw,  setResetPw]  = useState('');
  const [showReset,setShowReset]= useState(false);
  const [showPw,   setShowPw]   = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');
  const [error,    setError]    = useState('');

  const load = useCallback(async () => {
    const [accR, keysR] = await Promise.allSettled([
      axios.get(`${API}/admin/hostings/${hid}/account`,  { headers: authHeaders() }),
      axios.get(`${API}/admin/hostings/${hid}/api-keys`, { headers: authHeaders() }),
    ]);
    if (accR.status  === 'fulfilled') setAccount(accR.value.data);
    if (keysR.status === 'fulfilled') setApiKeys(keysR.value.data);
    setLoading(false);
  }, [hid, API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const createAccount = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await axios.post(`${API}/admin/hostings/${hid}/account`, invForm, { headers: authHeaders() });
      await load(); setMsg('Account erstellt');
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const removeAccount = async () => {
    if (!window.confirm('Account wirklich entfernen?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/hostings/${hid}/account`, { headers: authHeaders() });
      await load();
    } catch { } finally { setSaving(false); }
  };

  const doResetPw = async (e) => {
    e.preventDefault(); setSaving(true); setMsg(''); setError('');
    try {
      await axios.post(`${API}/admin/hostings/${hid}/reset-password`, { new_password: resetPw }, { headers: authHeaders() });
      setMsg('Passwort zurückgesetzt'); setResetPw(''); setShowReset(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const createKey = async () => {
    if (!newKey.name.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/hostings/${hid}/api-keys`, { name: newKey.name }, { headers: authHeaders() });
      setNewKey({ name:'', result: r.data });
      await load();
    } catch { } finally { setSaving(false); }
  };

  const deleteKey = async (kid) => {
    if (!window.confirm('API-Key löschen?')) return;
    await axios.delete(`${API}/admin/hostings/${hid}/api-keys/${kid}`, { headers: authHeaders() });
    await load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

      {/* Login Account */}
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 pb-3 border-b border-slate-800">
          <User className="w-4 h-4 text-cyan-400" /> Login-Account
        </h3>

        {error && <p className="text-sm text-red-400" data-testid="account-error">{error}</p>}
        {msg   && <p className="text-sm text-green-400">{msg}</p>}

        {account?.has_account ? (
          <div className="space-y-3">
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-sm space-y-2">
              <p className="text-green-400 font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5 mb-3">
                <UserCheck className="w-3.5 h-3.5" /> Account aktiv
              </p>
              {[['Name', account.name], ['E-Mail', account.email], ['Erstellt', account.created_at?.slice(0,10)]].map(([k,v]) => (
                <p key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="text-white font-medium">{v}</span></p>
              ))}
              <p className="flex justify-between">
                <span className="text-slate-400">Login-URL</span>
                <a href="/provider/login" className="text-cyan-400 hover:underline text-xs">/provider/login</a>
              </p>
            </div>

            {/* Passwort zurücksetzen */}
            {showReset ? (
              <form onSubmit={doResetPw} className="space-y-2">
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={resetPw}
                    onChange={e => setResetPw(e.target.value)} required minLength={8}
                    placeholder="Neues Passwort (min. 8 Zeichen)" data-testid="account-reset-pw-input"
                    className={inputCls + " pr-10"} />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={saving || resetPw.length < 8} data-testid="account-reset-pw-submit"
                    className="flex-1 py-2 bg-amber-500 text-slate-950 font-semibold rounded-lg hover:bg-amber-400 text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Speichern
                  </button>
                  <button type="button" onClick={() => { setShowReset(false); setResetPw(''); }}
                    className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm transition-colors">
                    Abbrechen
                  </button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowReset(true)} data-testid="show-reset-pw-btn"
                className="w-full py-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-semibold rounded-lg hover:bg-amber-500/20 text-sm flex items-center justify-center gap-2 transition-colors">
                <KeyRound className="w-4 h-4" /> Passwort zurücksetzen
              </button>
            )}

            <button onClick={removeAccount} disabled={saving} data-testid="remove-account-btn"
              className="w-full py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 font-semibold rounded-lg hover:bg-red-500/20 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              <UserMinus className="w-4 h-4" /> Account entfernen
            </button>
          </div>
        ) : (
          <form onSubmit={createAccount} className="space-y-3" data-testid="create-account-form">
            <p className="text-xs text-slate-500">Dieser Anbieter hat noch keinen Login-Zugang.</p>
            {[
              { k:'name',     lbl:'Name',     type:'text',     ph: 'Anbieter Team' },
              { k:'email',    lbl:'E-Mail',   type:'email',    ph: 'kontakt@anbieter.de' },
              { k:'password', lbl:'Passwort', type:'password', ph: 'Min. 8 Zeichen' },
            ].map(({ k, lbl, type, ph }) => (
              <div key={k}>
                <label className={labelCls}>{lbl}</label>
                <input type={type} value={invForm[k]} onChange={e => setInvForm(f => ({ ...f, [k]: e.target.value }))}
                  required placeholder={ph} data-testid={`invite-${k}`} className={inputCls} />
              </div>
            ))}
            <button type="submit" disabled={saving} data-testid="create-account-btn"
              className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Account erstellen
            </button>
          </form>
        )}
      </div>

      {/* API Keys */}
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 pb-3 border-b border-slate-800">
          <Key className="w-4 h-4 text-amber-400" /> API-Schlüssel ({apiKeys.length})
        </h3>

        {!account?.has_account ? (
          <p className="text-xs text-slate-500 py-4 text-center">Kein Account vorhanden – zuerst Account erstellen.</p>
        ) : (
          <>
            {/* New key result */}
            {newKey.result && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4" data-testid="new-key-result">
                <p className="text-xs font-semibold text-green-400 mb-1 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" /> Neuer Schlüssel (nur einmal sichtbar!)
                </p>
                <code className="text-xs text-slate-300 font-mono break-all">{newKey.result.key}</code>
                <button onClick={() => setNewKey({ name:'', result:null })}
                  className="mt-2 text-xs text-slate-500 hover:text-slate-300 block">Ausblenden</button>
              </div>
            )}

            {/* Create new key */}
            <div className="flex gap-2">
              <input value={newKey.name} onChange={e => setNewKey(k => ({ ...k, name: e.target.value }))}
                placeholder="Name des API-Keys" data-testid="new-key-name"
                className={inputCls + " flex-1"} />
              <button onClick={createKey} disabled={saving || !newKey.name.trim()} data-testid="create-key-btn"
                className="px-4 py-2 bg-amber-500 text-slate-950 font-semibold rounded-lg hover:bg-amber-400 text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Erstellen
              </button>
            </div>

            {apiKeys.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">Keine API-Schlüssel vorhanden.</p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map(k => (
                  <div key={k.id} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-800/60 border border-slate-700/60 rounded-lg"
                    data-testid={`api-key-row-${k.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{k.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{k.key_preview}</p>
                    </div>
                    <button onClick={() => deleteKey(k.id)} data-testid={`delete-key-${k.id}`}
                      className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Bewertungen Tab ──────────────────────────────────────────────────────── */
const STATUS_COLORS = { pending:'text-amber-400 bg-amber-500/10 border-amber-500/20', approved:'text-green-400 bg-green-500/10 border-green-500/20', rejected:'text-red-400 bg-red-500/10 border-red-500/20' };
const STATUS_LABELS = { pending:'Ausstehend', approved:'Genehmigt', rejected:'Abgelehnt' };

function ReviewsTab({ hid, authHeaders, API }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/hostings/${hid}/reviews`, { headers: authHeaders() });
    setReviews(r.data); setLoading(false);
  }, [hid, API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (rid, status) => {
    await axios.put(`${API}/admin/reviews/${rid}/status`, { status }, { headers: authHeaders() });
    load();
  };

  const del = async (rid) => {
    if (!window.confirm('Bewertung löschen?')) return;
    await axios.delete(`${API}/admin/reviews/${rid}`, { headers: authHeaders() });
    load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;
  if (!reviews.length) return <p className="text-slate-500 text-sm text-center py-12">Noch keine Bewertungen vorhanden.</p>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-400">{reviews.length} Bewertung{reviews.length !== 1 ? 'en' : ''}</p>
      {reviews.map(r => (
        <div key={r.id} className="bg-[#0f172a] border border-slate-800 rounded-xl p-4 space-y-3" data-testid={`review-row-${r.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.stars ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
                ))}
              </div>
              <span className="text-sm font-medium text-white">{r.title || '–'}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[r.status] || ''}`}>
                {STATUS_LABELS[r.status] || r.status}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {r.status !== 'approved' && (
                <button onClick={() => setStatus(r.id, 'approved')} data-testid={`approve-${r.id}`}
                  className="p-1.5 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors" title="Genehmigen">
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}
              {r.status !== 'rejected' && (
                <button onClick={() => setStatus(r.id, 'rejected')} data-testid={`reject-${r.id}`}
                  className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Ablehnen">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => del(r.id)} data-testid={`delete-review-${r.id}`}
                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">{r.comment}</p>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>von <span className="text-slate-400">{r.author}</span></span>
            <span>{r.created_at?.slice(0,10)}</span>
          </div>
          {r.provider_reply && (
            <div className="pl-3 border-l-2 border-cyan-500/30">
              <p className="text-xs text-cyan-400 mb-1 font-medium">Anbieter-Antwort</p>
              <p className="text-xs text-slate-400">{r.provider_reply}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────────── */
export function AdminProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeaders, API } = useAdminAuth();
  const [provider, setProvider] = useState(null);
  const [tab, setTab] = useState('profile');

  useEffect(() => {
    axios.get(`${API}/admin/hostings/${id}`, { headers: authHeaders() })
      .then(r => setProvider(r.data))
      .catch(() => navigate('/admin/providers'));
  }, [id, API, authHeaders, navigate]);

  if (!provider) return (
    <AdminLayout>
      <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full max-w-6xl">

        {/* Header */}
        <div className="mb-6">
          <button onClick={() => navigate('/admin/providers')}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-400 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
          </button>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {provider.logo_url ? (
                <img src={provider.logo_url} alt={provider.name}
                  className="w-12 h-12 rounded-xl object-contain bg-white/5 border border-slate-700 flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold flex-shrink-0">
                  {provider.name?.slice(0,2).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{provider.name}</h1>
                {provider.tagline && <p className="text-sm text-slate-500 mt-0.5">{provider.tagline}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {provider.url && (
                <a href={provider.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-sm hover:text-white transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> Website
                </a>
              )}
              <Link to={`/anbieter/${id}`} target="_blank"
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-sm hover:text-cyan-400 transition-colors">
                <Eye className="w-3.5 h-3.5" /> Öffentliche Seite
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-800 mb-6 overflow-x-auto">
          {TABS.map(({ id: tid, label, icon: Icon }) => (
            <button key={tid} onClick={() => setTab(tid)}
              data-testid={`tab-${tid}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === tid
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'profile'  && <ProfileTab  hid={id} authHeaders={authHeaders} API={API} />}
        {tab === 'products' && <ProductsTab hid={id} authHeaders={authHeaders} API={API} />}
        {tab === 'account'  && <AccountTab  hid={id} authHeaders={authHeaders} API={API} />}
        {tab === 'reviews'  && <ReviewsTab  hid={id} authHeaders={authHeaders} API={API} />}
      </div>
    </AdminLayout>
  );
}
